import { ReactComponent as IconBell } from 'assets/icons/IconBell.svg';
import { PATH_ROOT } from 'constants/api';
import { TOAST_CONFIG, TOAST_POSITION } from 'constants/toast';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { renderTitleNotification } from 'utils/functions/notification';
import { getUserProperty, toastInfo, toastInfoInviteUsingStorage, toastWarning } from 'utils/helpers';
import apiManagerNotification from '../api/meeting-notification';
import socket from "../api/websocket/socket";
import { ReactComponent as NoNotisBell } from '../assets/img//noNotisBell.svg';
import './Notification.css';
import NotificationList from './NotificationList';
import { TooltipCC } from './common';
import { USER_PROPERTIES } from 'constants/user';

// ── Sentinel objects used by NotificationList to render loading / end states ──
const LOADING_SENTINEL = Object.freeze({
    isLoading: true,
    id: "",
    roomId: "",
    userName: "",
    notificationType: 5,
    processStatus: 0,
    notifiedTime: "",
    messageObject: "",
    isSent: 1,
    unixNotifiedTime: 1692587715,
});

const NO_MORE_SENTINEL = Object.freeze({ lastNotification: true });

// ── Socket event names that share the same notification handler ──
const SOCKET_NOTIFICATION_EVENTS = [
    "meeting_notification",
    "mail_notification",
    "capacity_notification",
];

const PAGE_LIMIT = 10;

function NotificationBox() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [notis, setNotis] = useState([]);
    const [totalUnreadNotis, setTotalUnreadNotis] = useState(0);
    const [totalNotis, setTotalNotis] = useState(0);
    const [openNotificationBox, setOpenNotificationBox] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [idLargestRoom, setIdLargestRoom] = useState(0);
    const [messageToast, setMessageToast] = useState();

    const username = getUserProperty(USER_PROPERTIES.USERNAME);

    // Derived — no need for a separate state + useEffect
    const overloadTotalNotis = totalUnreadNotis > 99;

    const getAndSetAllNoti = useCallback(() => {
        if(username) {
            apiManagerNotification
                .getAllNotification(username, PAGE_LIMIT, idLargestRoom)
                .then((res) => {
                    setNotis(res?.data ?? []);
                    setTotalUnreadNotis(res?.countUnread ?? 0);
                    setTotalNotis(res?.data?.length ?? 0);
                })
                .catch(() => { /* silently ignore */ });
        }
    }, [username, idLargestRoom]);

    useEffect(() => {
        function handleDismiss(e) {
            if (e.key === 'Escape') {
                setOpenNotificationBox(false);
                return;
            }

            if (e.key === 'Enter') {
                const bellBox = document.getElementsByClassName('bell-box')[0];
                const isFocused = document.activeElement === bellBox;
                if (isFocused) setOpenNotificationBox((prev) => !prev);
                return;
            }

            // Tab — intentionally ignored
            if (e.key === 'Tab') return;

            // Click outside
            const el = document.getElementById('icon-bell-notis');
            const elNoOfNotis = document.getElementById('no-of-notis');
            const elBox = document.getElementsByClassName('notificationBox')[0];
            if (
                el && elBox &&
                e.target !== el &&
                !el?.contains(e.target) &&
                !elBox?.contains(e.target) &&
                !elNoOfNotis?.contains(e.target)
            ) {
                setOpenNotificationBox(false);
            }
        }

        window.addEventListener('click', handleDismiss);
        window.addEventListener('keydown', handleDismiss);
        return () => {
            window.removeEventListener('click', handleDismiss);
            window.removeEventListener('keydown', handleDismiss);
        };
    }, []);

    useEffect(() => {
        getAndSetAllNoti();

        if (socket === undefined) {
            window.location.reload();
            return;
        }

        const handleSocketNotification = (message) => {
            setMessageToast(message);
            getAndSetAllNoti();
        };

        SOCKET_NOTIFICATION_EVENTS.forEach((evt) =>
            socket.on(evt, handleSocketNotification),
        );

        return () => {
            SOCKET_NOTIFICATION_EVENTS.forEach((evt) =>
                socket.off(evt, handleSocketNotification),
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentPage <= 0) return;

        apiManagerNotification
            .getAllNotification(username, PAGE_LIMIT, idLargestRoom)
            .then((res) => {
                const data = res?.data;

                if (!data || data.length === 0) {
                    // No more data — append the "end" sentinel
                    setNotis((prev) => [
                        ...prev.filter((n) => !n?.isLoading && !n?.lastNotification),
                        NO_MORE_SENTINEL,
                    ]);
                    return;
                }

                setIdLargestRoom(data[data.length - 1]?.id);
                setTotalUnreadNotis(res?.countUnread ?? 0);
                setTotalNotis(data.length);

                const hasMore = data.length >= PAGE_LIMIT;
                const tail = hasMore ? LOADING_SENTINEL : NO_MORE_SENTINEL;

                setNotis((prev) => [
                    ...prev.filter((n) => !n?.isLoading && !n?.lastNotification),
                    ...data,
                    tail,
                ]);
            })
            .catch((e) => {
                console.log("ERROR fetch list notis", e);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const handleOpenNotiBox = useCallback(() => {
        setOpenNotificationBox((prev) => {
            if (prev) return false; // was open → close

            // was closed → open & refresh
            apiManagerNotification
                .getAllNotification(username, PAGE_LIMIT, 0)
                .then((res) => {
                    const data = res?.data;

                    if (!data) {
                        setTotalUnreadNotis(0);
                        setTotalNotis(0);
                        setNotis([]);
                        return;
                    }

                    setIdLargestRoom(data[data.length - 1]?.id ?? 0);
                    setTotalUnreadNotis(res?.countUnread ?? 0);
                    setTotalNotis(data.length);

                    const hasMore = data.length >= PAGE_LIMIT;
                    const tail = hasMore ? LOADING_SENTINEL : NO_MORE_SENTINEL;
                    setNotis([...data, tail]);
                })
                .catch(() => { /* silently ignore */ });

            return true;
        });
    }, [username]);

    const handleUpdateSentNoti = useCallback((id) => {
        if (id === 'all') {
            setNotis((prev) =>
                prev.map((noti) => ({ ...noti, processStatus: 1 })),
            );
            setTotalUnreadNotis(0);
            apiManagerNotification.updateAllNotificationStatus({
                userName: username,
                status: 1,
            });
        } else {
            setNotis((prev) =>
                prev.map((noti) =>
                    noti.id === id ? { ...noti, processStatus: 1 } : noti,
                ),
            );
            setTotalUnreadNotis((prev) => Math.max(0, prev - 1));
            apiManagerNotification.updateManyNotificationStatus({
                notificationId: [id],
                status: 1,
            });
        }
    }, [username]);

    const handleRedirectStoragePage = useCallback(() => {
        navigate(`${PATH_ROOT}/manager-storage`);
    }, [navigate]);

    useEffect(() => {
        if (!messageToast) return;

        if (messageToast?.notificationType === 11) {
            const title = renderTitleNotification(
                `${messageToast.notificationType}-${messageToast.warning_level}`,
            );
            toastWarning(
                title,
                +messageToast.warning_level === 2
                    ? t("meetingNotification.contentoverQuotaStorageLv2")
                    : t("meetingNotification.contentoverQuotaStorageLv3"),
                { ...TOAST_CONFIG, position: TOAST_POSITION.topCenter, autoClose: false },
                handleRedirectStoragePage,
            );
            return;
        }

        const { title, content } = renderTitleNotification(messageToast.notificationType);

        if (messageToast.notificationType === 12) {
            toastInfoInviteUsingStorage(
                title,
                content,
                { ...TOAST_CONFIG, position: TOAST_POSITION.topCenter, autoClose: false },
                navigate,
            );
        } else {
            toastInfo(title, content, {
                ...TOAST_CONFIG,
                position: TOAST_POSITION.topCenter,
                autoClose: messageToast.notificationType === 11 ? false : TOAST_CONFIG.autoClose,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageToast]);

    const unreadBadge = useMemo(() => {
        if (totalUnreadNotis < 1) return null;
        return (
            <div
                id="no-of-notis"
                className={overloadTotalNotis ? "overload-total-notis" : "total-notis"}
            >
                {overloadTotalNotis ? "99+" : totalUnreadNotis}
            </div>
        );
    }, [totalUnreadNotis, overloadTotalNotis]);

    return (
        <>
            <TooltipCC title={t('meetingNotification.bellTooltip')}>
                <div className="bell-box" onClick={handleOpenNotiBox} tabIndex={1}>
                    <IconBell
                        id="icon-bell-notis"
                        className={`icon-bell-noti is-animating ${openNotificationBox ? 'bell-box-active' : ''}`}
                    />
                    {unreadBadge}
                </div>
            </TooltipCC>

            {openNotificationBox && (
                <div className="notificationBox">
                    <div className="notificationListHeader">
                        <div className="notiHeader">{t('meetingNotification.notiHeader')}</div>
                        {totalNotis >= 1 && (
                            <TooltipCC title={t('meetingNotification.markAsReadAllTooltip')}>
                                <div
                                    className="markAsAllRead"
                                    tabIndex={1}
                                    onClick={() => handleUpdateSentNoti('all')}
                                    onKeyPress={() => handleUpdateSentNoti('all')}
                                >
                                    {t('meetingNotification.markAsAllRead')}
                                </div>
                            </TooltipCC>
                        )}
                    </div>

                    {totalNotis >= 1 ? (
                        <NotificationList
                            notis={notis}
                            handleUpdateSentNoti={handleUpdateSentNoti}
                            setOpenNotificationBox={setOpenNotificationBox}
                            setCurrentPage={setCurrentPage}
                        />
                    ) : (
                        <div className="zeroNumberOfNotis">
                            <NoNotisBell />
                            <div style={{ marginBottom: "1.5em", marginTop: "2.5em" }}>
                                {t('meetingNotification.zeroNumberOfNotis')}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

export default NotificationBox;
